<template>
  <div class="admin-dashboard">
    <!-- 顶部统计卡片 -->
    <div class="stats-cards">
      <el-row :gutter="20">
        <el-col :span="6">
          <div class="stat-card families">
            <div class="stat-icon">
              <i class="el-icon-house"></i>
            </div>
            <div class="stat-content">
              <div class="stat-number">{{ statistics.totalFamilies || 0 }}</div>
              <div class="stat-label">总家庭数</div>
            </div>
          </div>
        </el-col>
        <el-col :span="6">
          <div class="stat-card members">
            <div class="stat-icon">
              <i class="el-icon-user"></i>
            </div>
            <div class="stat-content">
              <div class="stat-number">{{ statistics.totalMembers || 0 }}</div>
              <div class="stat-label">总成员数</div>
            </div>
          </div>
        </el-col>
        <el-col :span="6">
          <div class="stat-card devices">
            <div class="stat-icon">
              <i class="el-icon-mobile"></i>
            </div>
            <div class="stat-content">
              <div class="stat-number">{{ statistics.totalDevices || 0 }}</div>
              <div class="stat-label">总设备数</div>
            </div>
          </div>
        </el-col>
        <el-col :span="6">
          <div class="stat-card unbound">
            <div class="stat-icon">
              <i class="el-icon-warning"></i>
            </div>
            <div class="stat-content">
              <div class="stat-number">{{ deviceStats.unboundDevices || 0 }}</div>
              <div class="stat-label">未绑定设备</div>
            </div>
          </div>
        </el-col>
      </el-row>
    </div>

    <!-- 主要功能区 -->
    <div class="main-content">
      <el-row :gutter="20">
        <!-- 家庭管理区块 -->
        <el-col :span="12">
          <div class="management-card">
            <div class="card-header">
              <h3>家庭管理</h3>
              <el-button type="primary" size="small" @click="goToFamilyManagement">
                查看全部
              </el-button>
            </div>
            <div class="card-content">
              <div class="management-stats">
                <div class="stat-item">
                  <span class="label">平均成员数:</span>
                  <span class="value">{{ (statistics.avgMembersPerFamily || 0).toFixed(1) }}人</span>
                </div>
                <div class="stat-item">
                  <span class="label">平均设备数:</span>
                  <span class="value">{{ (statistics.avgDevicesPerFamily || 0).toFixed(1) }}台</span>
                </div>
              </div>
              
              <!-- 最新家庭列表 -->
              <div class="recent-families">
                <h4>最新注册家庭</h4>
                <div class="family-list" v-loading="familyLoading">
                  <div 
                    v-for="family in recentFamilies" 
                    :key="family.id"
                    class="family-item"
                    @click="viewFamilyDetail(family.id)"
                  >
                    <div class="family-info">
                      <div class="family-name">{{ family.familyName }}</div>
                      <div class="family-meta">
                        <span>{{ family.memberCount || 0 }}名成员</span>
                        <span>{{ family.deviceCount || 0 }}台设备</span>
                      </div>
                    </div>
                    <div class="family-actions">
                      <i class="el-icon-arrow-right"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </el-col>

        <!-- 设备管理区块 -->
        <el-col :span="12">
          <div class="management-card">
            <div class="card-header">
              <h3>设备管理</h3>
              <el-button type="success" size="small" @click="goToDeviceManagement">
                管理设备
              </el-button>
            </div>
            <div class="card-content">
              <!-- 设备状态统计 -->
              <div class="device-status-chart">
                <h4>设备状态分布</h4>
                <div class="status-bars" v-loading="deviceLoading">
                  <div class="status-bar">
                    <div class="status-label">在线设备</div>
                    <div class="status-progress">
                      <div 
                        class="progress-bar online" 
                        :style="{ width: getPercentage(deviceStats.onlineDevices, deviceStats.totalDevices) + '%' }"
                      ></div>
                    </div>
                    <div class="status-count">{{ deviceStats.onlineDevices || 0 }}</div>
                  </div>
                  <div class="status-bar">
                    <div class="status-label">离线设备</div>
                    <div class="status-progress">
                      <div 
                        class="progress-bar offline" 
                        :style="{ width: getPercentage(deviceStats.offlineDevices, deviceStats.totalDevices) + '%' }"
                      ></div>
                    </div>
                    <div class="status-count">{{ deviceStats.offlineDevices || 0 }}</div>
                  </div>
                  <div class="status-bar">
                    <div class="status-label">未绑定设备</div>
                    <div class="status-progress">
                      <div 
                        class="progress-bar unbound" 
                        :style="{ width: getPercentage(deviceStats.unboundDevices, deviceStats.totalDevices) + '%' }"
                      ></div>
                    </div>
                    <div class="status-count">{{ deviceStats.unboundDevices || 0 }}</div>
                  </div>
                </div>
              </div>

              <!-- 快捷操作 -->
              <div class="quick-actions">
                <el-button type="primary" size="small" @click="showAddDeviceDialog">
                  添加设备
                </el-button>
                <el-button type="warning" size="small" @click="goToUnboundDevices">
                  未绑定设备
                </el-button>
              </div>
            </div>
          </div>
        </el-col>
      </el-row>
    </div>

    <!-- 添加设备对话框 -->
    <el-dialog
      title="添加设备"
      v-model="addDeviceDialogVisible"
      width="600px"
      :before-close="handleCloseAddDevice"
    >
      <el-form :model="deviceForm" :rules="deviceRules" ref="deviceForm" label-width="100px">
        <el-form-item label="设备编码" prop="deviceCode">
          <el-input v-model="deviceForm.deviceCode" placeholder="请输入设备编码" />
        </el-form-item>
        <el-form-item label="设备名称" prop="deviceName">
          <el-input v-model="deviceForm.deviceName" placeholder="请输入设备名称" />
        </el-form-item>
        <el-form-item label="产品" prop="productId">
          <el-select 
            v-model="deviceForm.productId" 
            placeholder="请选择产品" 
            style="width: 100%"
            @change="onProductChange"
          >
            <el-option
              v-for="product in products"
              :key="product.id"
              :label="product.name"
              :value="product.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="设备型号">
          <el-input v-model="deviceForm.deviceModel" placeholder="请输入设备型号" />
        </el-form-item>
        <el-form-item label="描述">
          <el-input 
            v-model="deviceForm.description" 
            type="textarea" 
            :rows="3"
            placeholder="请输入设备描述"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="addDeviceDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="handleAddDevice" :loading="addDeviceLoading">
            确定
          </el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'AdminDashboard',
  data() {
    return {
      statistics: {},
      deviceStats: {},
      recentFamilies: [],
      products: [],
      familyLoading: false,
      deviceLoading: false,
      
      // 添加设备相关
      addDeviceDialogVisible: false,
      addDeviceLoading: false,
      deviceForm: {
        deviceCode: '',
        deviceName: '',
        productId: '',
        deviceModel: '',
        description: ''
      },
      deviceRules: {
        deviceCode: [
          { required: true, message: '请输入设备编码', trigger: 'blur' }
        ],
        deviceName: [
          { required: true, message: '请输入设备名称', trigger: 'blur' }
        ],
        productId: [
          { required: true, message: '请选择产品', trigger: 'change' }
        ]
      }
    }
  },
  created() {
    this.loadDashboardData()
  },
  methods: {
    async loadDashboardData() {
      try {
        await Promise.all([
          this.loadFamilyStatistics(),
          this.loadDeviceStatistics(),
          this.loadRecentFamilies(),
          this.loadProducts()
        ])
      } catch (error) {
        console.error('加载仪表板数据失败:', error)
      }
    },

    async loadFamilyStatistics() {
      try {
        this.familyLoading = true
        const response = await this.$http.get('/admin/api/families/statistics')
        if (response.data.success) {
          this.statistics = response.data.data
        }
      } catch (error) {
        console.error('加载家庭统计失败:', error)
      } finally {
        this.familyLoading = false
      }
    },

    async loadDeviceStatistics() {
      try {
        this.deviceLoading = true
        const response = await this.$http.get('/admin/api/devices/statistics')
        if (response.data.success) {
          this.deviceStats = response.data.data
        }
      } catch (error) {
        console.error('加载设备统计失败:', error)
      } finally {
        this.deviceLoading = false
      }
    },

    async loadRecentFamilies() {
      try {
        const response = await this.$http.get('/admin/api/families/list')
        if (response.data.success) {
          this.recentFamilies = response.data.data.slice(0, 5) // 只显示前5个
        }
      } catch (error) {
        console.error('加载最新家庭失败:', error)
      }
    },

    async loadProducts() {
      try {
        const response = await this.$http.get('/api/products')
        if (response.data.success) {
          this.products = response.data.data
        }
      } catch (error) {
        console.error('加载产品列表失败:', error)
      }
    },

    onProductChange(productId) {
      if (productId) {
        const selectedProduct = this.products.find(p => p.id === productId)
        if (selectedProduct) {
          this.deviceForm.deviceModel = selectedProduct.model
        }
      } else {
        this.deviceForm.deviceModel = ''
      }
    },

    getPercentage(value, total) {
      if (!total || total === 0) return 0
      return Math.round((value / total) * 100)
    },

    goToFamilyManagement() {
      this.$router.push('/admin/families')
    },

    goToDeviceManagement() {
      this.$router.push('/admin/devices')
    },

    goToUnboundDevices() {
      this.$router.push('/admin/devices?filter=unbound')
    },

    viewFamilyDetail(familyId) {
      this.$router.push(`/admin/families/${familyId}`)
    },

    showAddDeviceDialog() {
      this.addDeviceDialogVisible = true
    },

    handleCloseAddDevice() {
      this.addDeviceDialogVisible = false
      this.resetDeviceForm()
    },

    resetDeviceForm() {
      this.deviceForm = {
        deviceCode: '',
        deviceName: '',
        productId: '',
        deviceModel: '',
        description: ''
      }
      this.$nextTick(() => {
        this.$refs.deviceForm?.clearValidate()
      })
    },

    async handleAddDevice() {
      try {
        const valid = await this.$refs.deviceForm.validate()
        if (!valid) return

        this.addDeviceLoading = true
        const response = await this.$http.post('/admin/api/devices/add', this.deviceForm)
        
        if (response.data.success) {
          this.$message.success('设备添加成功')
          this.addDeviceDialogVisible = false
          this.resetDeviceForm()
          // 刷新统计数据
          this.loadDeviceStatistics()
        } else {
          this.$message.error(response.data.message || '设备添加失败')
        }
      } catch (error) {
        console.error('添加设备失败:', error)
        this.$message.error(error.response?.data?.message || '添加设备失败')
      } finally {
        this.addDeviceLoading = false
      }
    },

    resetDeviceForm() {
      this.deviceForm = {
        deviceCode: '',
        deviceName: '',
        productId: '',
        deviceModel: '',
        description: ''
      }
      this.$nextTick(() => {
        this.$refs.deviceForm?.clearValidate()
      })
    },

    handleCloseAddDevice() {
      this.resetDeviceForm()
      this.addDeviceDialogVisible = false
    }
  }
}
</script>

<style scoped>
.admin-dashboard {
  padding: 20px;
}

/* 统计卡片 */
.stats-cards {
  margin-bottom: 30px;
}

.stat-card {
  background: #fff;
  border-radius: 12px;
  padding: 30px 20px;
  display: flex;
  align-items: center;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
}

.stat-card:hover {
  transform: translateY(-5px);
}

.stat-card.families .stat-icon {
  background: linear-gradient(45deg, #667eea, #764ba2);
}

.stat-card.members .stat-icon {
  background: linear-gradient(45deg, #f093fb, #f5576c);
}

.stat-card.devices .stat-icon {
  background: linear-gradient(45deg, #4facfe, #00f2fe);
}

.stat-card.unbound .stat-icon {
  background: linear-gradient(45deg, #fa709a, #fee140);
}

.stat-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 20px;
}

.stat-icon i {
  font-size: 24px;
  color: #fff;
}

.stat-content {
  flex: 1;
}

.stat-number {
  font-size: 32px;
  font-weight: bold;
  color: #333;
  margin-bottom: 5px;
}

.stat-label {
  color: #666;
  font-size: 14px;
}

/* 管理卡片 */
.main-content {
  margin-top: 20px;
}

.management-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  height: 500px;
  display: flex;
  flex-direction: column;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 25px;
  border-bottom: 1px solid #eee;
}

.card-header h3 {
  margin: 0;
  color: #333;
  font-size: 18px;
}

.card-content {
  flex: 1;
  padding: 25px;
  overflow-y: auto;
}

.management-stats {
  display: flex;
  gap: 30px;
  margin-bottom: 25px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
}

.stat-item {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.stat-item .label {
  font-size: 14px;
  color: #666;
}

.stat-item .value {
  font-size: 18px;
  font-weight: bold;
  color: #333;
}

/* 最新家庭列表 */
.recent-families h4 {
  margin: 0 0 15px 0;
  color: #333;
  font-size: 16px;
}

.family-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.family-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.family-item:hover {
  background: #e9ecef;
  transform: translateX(5px);
}

.family-info .family-name {
  font-size: 16px;
  font-weight: 500;
  color: #333;
  margin-bottom: 5px;
}

.family-info .family-meta {
  font-size: 12px;
  color: #666;
  display: flex;
  gap: 15px;
}

.family-actions i {
  color: #999;
  font-size: 16px;
}

/* 设备状态图表 */
.device-status-chart h4 {
  margin: 0 0 20px 0;
  color: #333;
  font-size: 16px;
}

.status-bars {
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-bottom: 25px;
}

.status-bar {
  display: flex;
  align-items: center;
  gap: 15px;
}

.status-label {
  width: 80px;
  font-size: 14px;
  color: #666;
}

.status-progress {
  flex: 1;
  height: 20px;
  background: #f0f0f0;
  border-radius: 10px;
  overflow: hidden;
}

.progress-bar {
  height: 100%;
  border-radius: 10px;
  transition: width 0.8s ease;
}

.progress-bar.online {
  background: linear-gradient(90deg, #52c41a, #73d13d);
}

.progress-bar.offline {
  background: linear-gradient(90deg, #ff4d4f, #ff7875);
}

.progress-bar.unbound {
  background: linear-gradient(90deg, #fa8c16, #ffa940);
}

.status-count {
  width: 40px;
  text-align: right;
  font-weight: bold;
  color: #333;
}

.quick-actions {
  display: flex;
  gap: 10px;
}

/* 对话框样式 */
.dialog-footer {
  text-align: right;
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .stats-cards .el-col {
    margin-bottom: 20px;
  }
  
  .main-content .el-col {
    margin-bottom: 20px;
  }
  
  .management-card {
    height: auto;
    min-height: 400px;
  }
}

@media (max-width: 768px) {
  .admin-dashboard {
    padding: 15px;
  }
  
  .management-stats {
    flex-direction: column;
    gap: 15px;
  }
  
  .stat-card {
    padding: 20px 15px;
  }
  
  .stat-number {
    font-size: 24px;
  }
}
</style>